import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  LocalOffer as LocalOfferIcon,
  AttachMoney as AttachMoneyIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  ZoomIn as ZoomInIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { getShopifyProduct, deleteShopifyProduct } from '../services/shopifyAPIService';

const ShopifyProductDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    if (productId) {
      fetchProductDetails();
    }
  }, [productId]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await getShopifyProduct(productId);

      let productData = null;

      // ✅ Handle backend response structure: { IsSuccess, Data }
      if (response?.IsSuccess && response?.Data) {
        productData = response.Data;
      } else if (response?.payload) {
        productData = response.payload;
      } else if (response?.data) {
        productData = response.data;
      } else {
        productData = response;
      }

      setProduct(productData);
    } catch (error) {
      console.error('❌ Error fetching product details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load product details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${product?.Title}" from Shopify?`)) {
      return;
    }

    try {
      await deleteShopifyProduct(productId);
      toast.success('Product deleted successfully from Shopify');
      navigate('/shopify/products');
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error('Failed to delete product from Shopify');
    }
  };

  const handleEdit = () => {
    navigate(`/shopify/products/edit/${productId}`);
  };

  const handleBack = () => {
    navigate('/shopify/products');
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setImageDialogOpen(true);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? (product?.Images?.length || 1) - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev === (product?.Images?.length || 1) - 1 ? 0 : prev + 1));
  };

  const handleCopyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'info';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading product details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Product not found'}
        </Alert>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Products
        </Button>
      </Container>
    );
  }

  const images = product.Images || [];
  const variants = product.Variants || [];
  const options = product.Options || [];
  const totalInventory = variants.reduce((sum, v) => sum + (v.InventoryQuantity || 0), 0);
  const prices = variants.map(v => v.Price || 0);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {product.Title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Chip
                label={product.Status || 'draft'}
                size="small"
                color={getStatusColor(product.Status)}
              />
              <Typography variant="body2" color="text.secondary">
                Product ID: {product.Id}
                <IconButton
                  size="small"
                  onClick={() => handleCopyToClipboard(product.Id.toString(), 'Product ID')}
                  sx={{ ml: 0.5 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Edit Product
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Images */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, position: 'sticky', top: 80 }}>
            {images.length > 0 ? (
              <>
                {/* Main Image */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    cursor: 'zoom-in',
                    mb: 2,
                  }}
                  onClick={() => handleImageClick(selectedImageIndex)}
                >
                  <img
                    src={images[selectedImageIndex]?.Src}
                    alt={product.Title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'white' },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageClick(selectedImageIndex);
                    }}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </Box>

                {/* Thumbnail Images */}
                {images.length > 1 && (
                  <Grid container spacing={1}>
                    {images.map((image, index) => (
                      <Grid item xs={3} key={image.Id}>
                        <Box
                          onClick={() => setSelectedImageIndex(index)}
                          sx={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '100%',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: selectedImageIndex === index ? 3 : 1,
                            borderColor: selectedImageIndex === index ? 'primary.main' : 'grey.300',
                            '&:hover': {
                              borderColor: 'primary.main',
                            },
                          }}
                        >
                          <img
                            src={image.Src}
                            alt={`${product.Title} ${index + 1}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 300,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                }}
              >
                <ImageIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No images available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Details */}
        <Grid item xs={12} md={7}>
          {/* Quick Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ShoppingCartIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {variants.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Variants
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <InventoryIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {totalInventory}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    In Stock
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AttachMoneyIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    ${minPrice.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Starting At
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ImageIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {images.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Images
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Details" />
              <Tab label="Variants" />
              <Tab label="Options" />
            </Tabs>

            {/* Tab Panel 0: Details */}
            {currentTab === 0 && (
              <Box sx={{ p: 3 }}>
                {/* Basic Information */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Basic Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Title"
                      secondary={product.Title}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  <Divider component="li" />
                  
                  {product.BodyHtml && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Description"
                          secondary={
                            <Box
                              dangerouslySetInnerHTML={{ __html: product.BodyHtml }}
                              sx={{ mt: 1 }}
                            />
                          }
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </>
                  )}

                  <ListItem>
                    <ListItemText
                      primary="Vendor"
                      secondary={product.Vendor || 'N/A'}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    <StoreIcon color="action" />
                  </ListItem>
                  <Divider component="li" />

                  <ListItem>
                    <ListItemText
                      primary="Product Type"
                      secondary={product.ProductType || 'N/A'}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    <CategoryIcon color="action" />
                  </ListItem>
                  <Divider component="li" />

                  <ListItem>
                    <ListItemText
                      primary="Status"
                      secondary={
                        <Chip
                          label={product.Status}
                          size="small"
                          color={getStatusColor(product.Status)}
                          sx={{ mt: 0.5 }}
                        />
                      }
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  <Divider component="li" />

                  {product.Tags && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Tags"
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                              {product.Tags.split(',').map((tag, index) => (
                                <Chip
                                  key={index}
                                  label={tag.trim()}
                                  size="small"
                                  variant="outlined"
                                  icon={<LocalOfferIcon />}
                                />
                              ))}
                            </Box>
                          }
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </>
                  )}

                  <ListItem>
                    <ListItemText
                      primary="URL Handle"
                      secondary={product.Handle || 'N/A'}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(product.Handle, 'URL Handle')}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                  <Divider component="li" />

                  <ListItem>
                    <ListItemText
                      primary="Created At"
                      secondary={formatDate(product.CreatedAt)}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  <Divider component="li" />

                  <ListItem>
                    <ListItemText
                      primary="Updated At"
                      secondary={formatDate(product.UpdatedAt)}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                </List>

                {/* SEO Information */}
                {(product.SeoTitle || product.SeoDescription) && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      SEO Information
                    </Typography>
                    <List>
                      {product.SeoTitle && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="SEO Title"
                              secondary={product.SeoTitle}
                              primaryTypographyProps={{ fontWeight: 600 }}
                            />
                          </ListItem>
                          <Divider component="li" />
                        </>
                      )}
                      {product.SeoDescription && (
                        <ListItem>
                          <ListItemText
                            primary="SEO Description"
                            secondary={product.SeoDescription}
                            primaryTypographyProps={{ fontWeight: 600 }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}
              </Box>
            )}

            {/* Tab Panel 1: Variants */}
            {currentTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Product Variants ({variants.length})
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Compare At</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Inventory</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Weight</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {variants.map((variant) => (
                        <TableRow key={variant.Id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {variant.Title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {variant.Id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              ${variant.Price?.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {variant.CompareAtPrice ? (
                              <Typography variant="body2" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                                ${variant.CompareAtPrice.toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{variant.Sku || 'N/A'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{variant.Barcode || 'N/A'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={variant.InventoryQuantity}
                              size="small"
                              color={variant.InventoryQuantity > 0 ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {variant.Weight ? `${variant.Weight} ${variant.WeightUnit}` : 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Variant Details */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Additional Variant Information
                  </Typography>
                  <Grid container spacing={2}>
                    {variants.map((variant) => (
                      <Grid item xs={12} sm={6} key={variant.Id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              {variant.Title}
                            </Typography>
                            <List dense>
                              <ListItem>
                                <ListItemText
                                  primary="Requires Shipping"
                                  secondary={
                                    variant.RequiresShipping ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                      <CancelIcon color="error" fontSize="small" />
                                    )
                                  }
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemText
                                  primary="Taxable"
                                  secondary={
                                    variant.Taxable ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                      <CancelIcon color="error" fontSize="small" />
                                    )
                                  }
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemText
                                  primary="Inventory Policy"
                                  secondary={variant.InventoryPolicy}
                                />
                              </ListItem>
                              <ListItem>
                                <ListItemText
                                  primary="Inventory Management"
                                  secondary={variant.InventoryManagement}
                                />
                              </ListItem>
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            )}

            {/* Tab Panel 2: Options */}
            {currentTab === 2 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Product Options ({options.length})
                </Typography>

                {options.length > 0 ? (
                  <Grid container spacing={2}>
                    {options.map((option, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                              {option.Name}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {option.Values.map((value, valueIndex) => (
                                <Chip
                                  key={valueIndex}
                                  label={value}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info">No product options defined</Alert>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Image Zoom Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          {images.length > 0 && (
            <>
              <img
                src={images[selectedImageIndex]?.Src}
                alt={product.Title}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
              {images.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'white' },
                    }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'white' },
                    }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2">
                      {selectedImageIndex + 1} / {images.length}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ShopifyProductDetailPage;